/**
 * App-wide device logs -> app_logs (migration 135), kept 7 days.
 *
 * lynx_find_logs only records while Find or the map is open. This covers the rest: uncaught errors, a stalled main
 * thread, lifecycle and navigation, auth changes, and taps that land under the tab dock without reaching it - the
 * two leading explanations for the dock going dead (lib/keyboardScrollReset).
 *
 * Rows buffer in memory and in Preferences (so a kill or a dead zone loses nothing), and upload in batches through
 * the Find log transport. A row is owned by whoever is signed in when it uploads; signed out, rows wait.
 * Read them with v_app_logs (service role), ordered by client_at.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  APP_LOG_DISK_DEBOUNCE_MS,
  APP_LOG_FLUSH_INTERVAL_MS,
  APP_LOG_FLUSH_SIZE_THRESHOLD,
  APP_LOG_PREFERENCES_KEY,
  APP_LOG_RING_CAPACITY,
  APP_LOG_THROTTLE_MS,
  APP_REMOTE_LOGGING,
} from '../config/appRemoteLogging';
import { APP_BUILD_VERSION } from './query/cacheVersion';
import { uploadFindLogBatch } from './findRemoteLog/transport';
import { takeLastNativeExit } from './native/priiLifecycle';
import { getSupabase, setNativeSessionLogger } from '../supabaseClient';

const LEVELS = { debug: 10, info: 20, warn: 30, error: 40 };
const IS_NATIVE = Capacitor.isNativePlatform();
const PLATFORM = Capacitor.getPlatform();

const sessionId =
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
let entries = [];
let nextSeq = 1;
let restored = false;
let flushing = false;
let flushTimer = null;
let diskTimer = null;
const lastByKey = new Map();

/* Dev only: ?beacon=PORT posts every log line to http://localhost:PORT/log instead of app_logs, so a browser on the
   iOS Simulator (WebKit, like the phone) can be measured from this Mac. */
const BEACON =
  import.meta.env.DEV && typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('beacon')
    : null;

/** What a stall log should say about the moment before it (the 3D map registers itself while it has a scene). */
let stallContext = null;
export function setStallContext(fn) {
  stallContext = fn;
}

function active() {
  return (
    Boolean(BEACON) || (APP_REMOTE_LOGGING.enabled && (!APP_REMOTE_LOGGING.nativeOnly || IS_NATIVE))
  );
}

function currentRoute() {
  try {
    return window.location.pathname;
  } catch {
    return null;
  }
}

function persistSoon() {
  if (!IS_NATIVE) return;
  if (diskTimer != null) clearTimeout(diskTimer);
  diskTimer = setTimeout(() => {
    diskTimer = null;
    void persistNow();
  }, APP_LOG_DISK_DEBOUNCE_MS);
}

async function persistNow() {
  if (!IS_NATIVE) return;
  try {
    await Preferences.set({ key: APP_LOG_PREFERENCES_KEY, value: JSON.stringify({ entries }) });
  } catch {
    // disk full or plugin gone - the in-memory ring still holds them
  }
}

async function restore() {
  if (restored) return;
  restored = true;
  if (!IS_NATIVE) return;
  try {
    const { value } = await Preferences.get({ key: APP_LOG_PREFERENCES_KEY });
    const saved = value ? JSON.parse(value)?.entries : null;
    if (Array.isArray(saved) && saved.length > 0) {
      // Earlier launches keep their own session_id and seq; this launch's rows follow.
      entries = [...saved, ...entries].slice(-APP_LOG_RING_CAPACITY);
    }
  } catch {
    // unreadable tail - start fresh
  }
}

/**
 * @param {'debug'|'info'|'warn'|'error'} level
 * @param {string} eventName dotted, e.g. 'app.error'
 * @param {Record<string, unknown>} [payload]
 */
/** The last few things the app said, kept even when below the remote level: a resume gap names what ran just before it. */
const crumbs = [];
export function appLog(level, eventName, payload = {}) {
  crumbs.push([Date.now(), eventName]);
  if (crumbs.length > 12) crumbs.shift();
  if (!active()) return;
  if ((LEVELS[level] ?? 0) < (LEVELS[APP_REMOTE_LOGGING.minLevel] ?? 20)) return;
  if (BEACON) {
    try {
      navigator.sendBeacon(
        `http://localhost:${BEACON}/log`,
        JSON.stringify({
          t: Date.now(),
          level,
          eventName,
          route: window.location.pathname,
          payload,
        }),
      );
    } catch {
      /* dev only */
    }
    return;
  }

  const now = Date.now();
  const key = `${eventName}|${payload?.message ?? ''}`;
  const spacing = APP_LOG_THROTTLE_MS[eventName] ?? APP_LOG_THROTTLE_MS.default;
  const last = lastByKey.get(key);
  if (last && now - last.at < spacing) {
    last.row.payload = {
      ...last.row.payload,
      repeat_count: (last.row.payload.repeat_count ?? 1) + 1,
    };
    return;
  }

  const row = {
    session_id: sessionId,
    client_ts: now,
    seq: nextSeq++,
    level,
    event_name: eventName,
    payload: payload ?? {},
    route: currentRoute(),
    app_version: APP_BUILD_VERSION,
    platform: PLATFORM,
  };
  lastByKey.set(key, { at: now, row });
  entries.push(row);
  if (entries.length > APP_LOG_RING_CAPACITY) entries = entries.slice(-APP_LOG_RING_CAPACITY);
  persistSoon();
  if (entries.length >= APP_LOG_FLUSH_SIZE_THRESHOLD) void flushAppLogs('size');
}

export async function flushAppLogs(reason = 'manual') {
  if (!active() || flushing || entries.length === 0) return false;
  flushing = true;
  try {
    const { data } = await getSupabase().auth.getSession();
    const token = data?.session?.access_token;
    const userId = data?.session?.user?.id;
    if (!token || !userId) return false; // signed out: rows wait for a session

    while (entries.length > 0) {
      const batch = entries.slice(0, APP_LOG_FLUSH_SIZE_THRESHOLD);
      const rows = batch.map((r) => ({ ...r, user_id: userId }));
      const result = await uploadFindLogBatch(rows, token, { table: 'app_logs' });
      if (!result.ok) {
        // 4xx other than timeout/rate-limit will never succeed: drop so healthy rows behind it still land.
        const permanent =
          result.status >= 400 &&
          result.status < 500 &&
          result.status !== 408 &&
          result.status !== 429;
        if (permanent) {
          entries = entries.slice(batch.length);
          continue;
        }
        return false;
      }
      entries = entries.slice(batch.length);
    }
    return true;
  } catch {
    return false;
  } finally {
    flushing = false;
    persistSoon();
    if (reason === 'background') void persistNow();
  }
}

function truncate(text, max = 2000) {
  const s = String(text ?? '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * How the last session ended, read at the next launch (2026-10-12). iOS does not say when it kills an app, and a
 * killed app stops sharing location (Jen at the Bowl: 102 min frozen at her seat on Matt's screen). A launch whose
 * `prev.state` is 'background' means the last session ended while backgrounded - killed by iOS or swiped away;
 * `prev.aliveAt` is about when. Decides whether the map should hand its GPU memory back in the background.
 */
const LIFE_KEY = 'prii:last-life';
const LIFE_BEAT_MS = 30_000;
let lifeState = null;

function markLife(state) {
  if (state) lifeState = state;
  try {
    localStorage.setItem(
      LIFE_KEY,
      JSON.stringify({ state: lifeState, aliveAt: Date.now(), route: window.location.pathname }),
    );
  } catch {
    // private mode / storage full: the breadcrumb is a nice-to-have
  }
}

function readLastLife() {
  try {
    const prev = JSON.parse(localStorage.getItem(LIFE_KEY) ?? 'null');
    if (!prev || !Number.isFinite(prev.aliveAt)) return null;
    return { ...prev, goneForS: Math.round((Date.now() - prev.aliveAt) / 1000) };
  } catch {
    return null;
  }
}

/**
 * A page that loaded within this long of the last session's last sign of life is a reload, not an open: the web process
 * was replaced (iOS, 2026-10-29: four times in an evening, each right after the app came forward) and Capacitor loaded
 * the page again. Such a load goes back to the page you were on, with no launch splash.
 */
const QUICK_RELOAD_S = 90;
let quickReload = null; // decided once, at the first read, before the previous life is overwritten
/** The route to go back to when this load is a quick reload, else null. Read before installAppLog() runs. */
const RELOAD_RUN_KEY = 'prii:quick-reload-run';
export function quickReloadRoute() {
  if (quickReload !== null) return quickReload || null;
  // Native only: in a browser a URL typed within 90 s of the last page would be swapped for the old route.
  const prev = IS_NATIVE ? readLastLife() : null;
  let route =
    prev &&
    prev.goneForS <= QUICK_RELOAD_S &&
    typeof prev.route === 'string' &&
    prev.route.startsWith('/')
      ? prev.route
      : '';
  // A page that dies on arrival must not be restored into again (2026-10-30: a broken map build reloaded every 2 s,
  // and each reload went straight back to the map). The second quick reload in a row lands on "/" and clears the run.
  try {
    const run = route ? Number(localStorage.getItem(RELOAD_RUN_KEY) ?? 0) + 1 : 0;
    if (run >= 2) {
      route = '';
      localStorage.removeItem(RELOAD_RUN_KEY);
    } else if (run) localStorage.setItem(RELOAD_RUN_KEY, String(run));
    else localStorage.removeItem(RELOAD_RUN_KEY);
  } catch {
    /* no storage: restore once, as before */
  }
  quickReload = route;
  return route || null;
}

let installed = false;

/** Global hooks. Idempotent; call once at startup. */
export function installAppLog() {
  if (installed || typeof window === 'undefined' || !active()) return;
  installed = true;
  setNativeSessionLogger((event, payload) => appLog('info', event, payload));
  void restore().then(() => flushAppLogs('startup'));

  appLog('info', 'app.launch', {
    ua: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    dpr: window.devicePixelRatio,
    prev: readLastLife(),
    restored: quickReloadRoute(),
  });
  markLife(document.visibilityState === 'hidden' ? 'background' : 'foreground');
  // What ended the previous process, if the native tracer saw it: a clean exit with its backtrace (10-29: three of
  // them, no reason anywhere else). One row, then the file is gone.
  void takeLastNativeExit().then((exit) => {
    if (!exit) return;
    const { found, backtrace, ...rest } = exit;
    appLog('warn', 'app.native_exit', { ...rest, backtrace: truncate(backtrace, 6000) });
  });
  // Last sign of life: JS keeps running in the background while location is shared, so the last beat is roughly
  // when the process died.
  window.setInterval(() => {
    markLife(null);
    try {
      localStorage.removeItem(RELOAD_RUN_KEY);
    } catch {
      /* see quickReloadRoute */
    } // alive 30 s: not a reload loop
  }, LIFE_BEAT_MS);

  // Uncaught errors, including React 19's uncaught render errors (reported through window 'error').
  window.addEventListener('error', (e) => {
    appLog('error', 'app.error', {
      message: truncate(e.message, 500),
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : null,
      stack: truncate(e.error?.stack),
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    appLog('error', 'app.unhandled_rejection', {
      message: truncate(r?.message ?? r, 500),
      stack: truncate(r?.stack),
    });
  });

  // console.error is where most caught-and-logged failures already go.
  const originalError = console.error.bind(console);
  console.error = (...args) => {
    originalError(...args);
    try {
      const message = args
        .map((a) =>
          a instanceof Error ? a.message : typeof a === 'string' ? a : JSON.stringify(a),
        )
        .join(' ');
      const err = args.find((a) => a instanceof Error);
      appLog('warn', 'console.error', {
        message: truncate(message, 500),
        stack: truncate(err?.stack, 1000),
      });
    } catch {
      // never let logging throw from console.error
    }
  };

  // Lifecycle: flush on the way out.
  // app.resume: how long the app was away and how long until it drew its first frame back. The resume freeze
  // (2026-10-16) was a main-thread rebuild right after foreground; a slow first frame is that, measured.
  let hiddenAt = null;
  let expected = Date.now() + 1000; // when the stall timer below should next fire
  let wasVisible = document.visibilityState === 'visible';
  document.addEventListener('visibilitychange', () => {
    const hidden = document.visibilityState === 'hidden';
    appLog('info', hidden ? 'app.background' : 'app.foreground', {});
    markLife(hidden ? 'background' : 'foreground');
    if (hidden) {
      hiddenAt = Date.now();
      // A stall the app left the screen in was never logged: the timer below only counts one that ends while visible, and
      // giving up on a frozen map means swiping home (Sam, 2026-10-23: two minutes stuck on the Bowl, not one row). This
      // handler runs as late as the stall did, so the timer being late here is that stall.
      const late = hiddenAt - expected;
      if (late > 1500) {
        let ctx = null;
        try {
          ctx = stallContext?.() ?? null;
        } catch {
          /* a context that throws is not context */
        }
        appLog('warn', 'perf.main_thread_stall', {
          ms: late,
          endedHidden: true,
          ...(ctx ? { ctx } : {}),
        });
      }
      wasVisible = false; // the timer's first tick back is late by the time away, which is not a stall
      void flushAppLogs('background');
      return;
    }
    const shownAt = Date.now();
    const awayS = hiddenAt == null ? null : Math.round((shownAt - hiddenAt) / 1000);
    // On disk now: the four reloads of 2026-10-29 each lost this foreground row to the 2 s debounce.
    void persistNow();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        appLog('info', 'app.resume', { awayS, firstFrameMs: Date.now() - shownAt });
      }),
    );
    sampleResumeGaps(shownAt, awayS);
  });

  // perf.resume_gaps (2026-10-27): back after 11 h on the welcome Find, the main thread stalled 4.5 s with no slow map frame
  // and no GL release logged - nothing said what ran. For 8 s after a resume a chain of zero-delay timers notes every gap
  // over 250 ms and what the app last logged before it, and sends them in one line.
  function sampleResumeGaps(shownAt, awayS) {
    const gaps = [];
    let prev = Date.now();
    const tick = () => {
      const now = Date.now();
      if (now - prev > 250 && gaps.length < 8)
        gaps.push({
          atMs: prev - shownAt,
          ms: now - prev,
          after: crumbs
            .filter(([t]) => t <= prev)
            .slice(-3)
            .map(([, n]) => n),
        });
      prev = now;
      if (document.visibilityState !== 'visible') return;
      if (now - shownAt < 8000) setTimeout(tick, 0);
      else if (gaps.length)
        appLog('warn', 'perf.resume_gaps', {
          awayS,
          route: window.location.pathname + window.location.search,
          gaps,
        });
    };
    setTimeout(tick, 0);
  }

  // Slow drawing (2026-10-30): taps lagged seconds everywhere while JS was free - the screen itself was slow to draw (a
  // live blur on 75 animated background orbs). The stall timer above can't see that; frame gaps can. Every 30 s on screen,
  // if many frames were slow, perf.frames says how slow and where.
  {
    let last = 0,
      n = 0,
      slow = 0,
      worst = 0,
      since = performance.now();
    // Frames never fire while hidden, so the first one back measured the whole time away as a 371 s "frame"
    // (2026-10-29): a hide resets the window, and the first frame back starts a new one.
    document.addEventListener('visibilitychange', () => {
      last = 0;
      since = performance.now();
      n = 0;
      slow = 0;
      worst = 0;
    });
    const frame = (t) => {
      if (document.visibilityState === 'visible') {
        if (last) {
          const d = t - last;
          n += 1;
          if (d > 50) slow += 1;
          if (d > worst) worst = d;
        }
        last = t;
        if (t - since > 30000) {
          if (n > 0 && (slow / n > 0.1 || worst > 700))
            appLog('warn', 'perf.frames', {
              route: window.location.pathname,
              fps: Math.round(n / ((t - since) / 1000)),
              slowPct: Math.round((100 * slow) / n),
              worstMs: Math.round(worst),
            });
          n = 0;
          slow = 0;
          worst = 0;
          since = t;
        }
      } else {
        last = 0;
        since = t;
        n = 0;
        slow = 0;
        worst = 0;
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  // Main-thread stall: a 1 s timer that fires late means JS was blocked (taps need JS; native scrolling does not).
  expected = Date.now() + 1000;
  setInterval(() => {
    const now = Date.now();
    const late = now - expected;
    const visible = document.visibilityState === 'visible';
    if (visible && wasVisible && late > 1500) {
      let ctx = null;
      try {
        ctx = stallContext?.() ?? null;
      } catch {
        /* a context that throws is not context */
      }
      appLog('warn', 'perf.main_thread_stall', { ms: late, ...(ctx ? { ctx } : {}) });
    }
    wasVisible = visible;
    expected = now + 1000;
  }, 1000);

  // A tap that reached nothing but the page itself, anywhere: with the body fixed and every screen filling it,
  // html/body is never a legitimate target. Logged with the viewport state so the next dead-touch report has data.
  document.addEventListener(
    'pointerdown',
    (e) => {
      if (e.target !== document.documentElement && e.target !== document.body) return;
      appLog('warn', 'ui.dead_tap', {
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
        scrollY: window.scrollY,
        docScrollTop: document.documentElement.scrollTop,
        vvOffsetTop: window.visualViewport?.offsetTop ?? null,
        vvPageTop: window.visualViewport?.pageTop ?? null,
        vvHeight: window.visualViewport?.height ?? null,
        innerHeight: window.innerHeight,
        active: document.activeElement?.tagName ?? null,
      });
    },
    true,
  );

  // A tap inside the tab dock's box that did not reach the dock: the WKWebView hit-test offset (see
  // keyboardScrollReset). Records the scroll state that explains it.
  document.addEventListener(
    'pointerdown',
    (e) => {
      const dock = document.querySelector('.app-tab-dock');
      if (!dock) return;
      const r = dock.getBoundingClientRect();
      if (
        r.width === 0 ||
        e.clientX < r.left ||
        e.clientX > r.right ||
        e.clientY < r.top ||
        e.clientY > r.bottom
      )
        return;
      if (dock.contains(e.target)) return;
      const t = e.target instanceof Element ? e.target : null;
      appLog('warn', 'ui.dock.missed_tap', {
        target: t
          ? `${t.tagName.toLowerCase()}${t.className && typeof t.className === 'string' ? `.${t.className.split(' ')[0]}` : ''}`
          : null,
        scrollY: window.scrollY,
        docScrollTop: document.documentElement.scrollTop,
        vvOffsetTop: window.visualViewport?.offsetTop ?? null,
        vvHeight: window.visualViewport?.height ?? null,
        innerHeight: window.innerHeight,
        active: document.activeElement?.tagName ?? null,
      });
    },
    true,
  );

  // Auth transitions - sign-outs and failed refreshes are the ones worth reading later.
  try {
    getSupabase().auth.onAuthStateChange((event) => {
      if (event === 'INITIAL_SESSION') return;
      appLog('info', 'auth.state', { event });
      if (event === 'SIGNED_IN') void flushAppLogs('signed-in');
    });
  } catch {
    // client not ready - auth rows are a nice-to-have
  }

  flushTimer = setInterval(() => void flushAppLogs('interval'), APP_LOG_FLUSH_INTERVAL_MS);
}

/** For tests. */
export function _appLogState() {
  return { entries, sessionId, flushTimer };
}
